import { Link } from 'react-router-dom';
import {
  Calculator,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  X,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useGestaoDock } from '@/contexts/GestaoDockContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import GestaoWorkspace from '@/components/gestao/GestaoWorkspace';

export default function GestaoFloatingDock() {
  const { user } = useAuth();
  const { open, collapsed, openDock, closeDock, toggleCollapsed } = useGestaoDock();

  if (!user) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={openDock}
          className={cn(
            'fixed bottom-4 right-4 z-[100] flex h-14 w-14 items-center justify-center rounded-full',
            'bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-600/35',
            'ring-2 ring-white/25 dark:ring-white/10',
            'transition-transform hover:scale-[1.04] active:scale-[0.98]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-950',
          )}
          aria-label="Abrir Gestão de múltipla"
        >
          <Calculator className="h-6 w-6" strokeWidth={2.25} />
        </button>
      )}

      {open && (
        <div
          className={cn(
            'fixed bottom-4 right-4 z-[100] flex flex-col overflow-hidden rounded-2xl',
            'border border-slate-200/80 bg-white/95 shadow-2xl backdrop-blur-xl dark:border-white/[0.1] dark:bg-neutral-950/95',
            collapsed
              ? 'w-[min(100vw-2rem,380px)]'
              : 'max-h-[min(88vh,760px)] w-[min(100vw-1.25rem,460px)]',
          )}
          role="dialog"
          aria-label="Gestão de múltipla"
        >
          <div
            className={cn(
              'flex shrink-0 items-center gap-2 border-b border-slate-200/70 px-2 py-2 dark:border-white/[0.08]',
              collapsed ? 'justify-between' : 'justify-between',
            )}
          >
            <div className="flex min-w-0 items-center gap-2 pl-1">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-md shadow-emerald-600/25">
                <Calculator className="h-4 w-4" strokeWidth={2.5} />
              </div>
              <div className="min-w-0">
                <p className="truncate text-xs font-black uppercase tracking-wider text-slate-800 dark:text-white">
                  Gestão
                </p>
                <p className="truncate text-[10px] font-medium text-slate-500 dark:text-neutral-500">
                  Múltipla · painel flutuante
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-0.5">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-lg text-slate-600 dark:text-neutral-400"
                onClick={toggleCollapsed}
                aria-expanded={!collapsed}
                aria-label={collapsed ? 'Expandir painel' : 'Minimizar painel'}
              >
                {collapsed ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="hidden h-9 rounded-lg px-2 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400 sm:inline-flex"
                asChild
              >
                <Link to="/gestao" className="gap-1">
                  <ExternalLink className="h-3.5 w-3.5" />
                  Página
                </Link>
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-lg text-slate-500 hover:text-red-600 dark:text-neutral-400 dark:hover:text-red-400"
                onClick={closeDock}
                aria-label="Fechar painel"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {!collapsed && (
            <Link
              to="/gestao"
              className="shrink-0 border-b border-slate-100 px-3 py-2 text-center text-[11px] font-semibold text-emerald-700 dark:border-white/[0.06] dark:text-emerald-400 sm:hidden"
            >
              Abrir página completa
            </Link>
          )}

          {!collapsed && (
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-4 pt-3">
              <GestaoWorkspace embedMode="dock" />
            </div>
          )}
        </div>
      )}
    </>
  );
}
